import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import * as Icons from "lucide-react";
import Uploader from "@/components/Uploader";
import { Sparkles, Image as ImageIcon } from "lucide-react";
import { trackJobPost } from "@/lib/analytics";

const PREMIUM_PRICE_INR = 299;

export default function PostJob() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    title: "", category: "welder", description: "", location: "",
    salary_min: 500, salary_max: 1000, positions: 1, duration_days: 30, contact_phone: "",
    logo_url: "", logo_file_id: "", long_description: "", site_photo_ids: [], is_premium: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => {
      setCats(r.data);
      setForm((s) => ({ ...s, category: r.data[0]?.key || "welder" }));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const nameFor = (c) => c[`name_${lang}`] || c.name_en;
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const setNum = (k) => (e) => setForm((s) => ({ ...s, [k]: Number(e.target.value) || 0 }));

  const submit = async () => {
    if (!form.title || !form.description || !form.location) return toast.error("Please fill required fields");
    setBusy(true);
    try {
      const { data: job } = await api.post("/jobs", form);
      trackJobPost(form.category, form.is_premium);
      if (form.is_premium) {
        const prefill = { name: user?.name || "", email: user?.email || "", contact: user?.mobile || "" };
        const result = await openRazorpayCheckout({ packageId: "premium_job_post", jobId: job.id, prefill });
        if (result.ok) {
          nav(`/jobs/${job.id}`);
          return;
        }
        // Payment cancelled or failed — job is created but not premium
        toast.info("Job posted (payment skipped — you can upgrade any time from Plans)");
        nav(`/jobs/${job.id}`);
        return;
      }
      toast.success("Posted!");
      nav(`/jobs/${job.id}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <h1 className="font-display text-3xl font-extrabold sm:text-4xl">{t("post_job")}</h1>

      <div className="sarthi-card mt-6 p-6">
        <label className="mb-2 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t("categories")}</label>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {cats.map((c) => {
            const Icon = Icons[c.icon] || Icons.Wrench;
            const sel = form.category === c.key;
            return (
              <button
                key={c.key}
                data-testid={`post-cat-${c.key}`}
                onClick={() => setForm((s) => ({ ...s, category: c.key }))}
                className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 ${sel ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}
              >
                <Icon className="h-6 w-6" />
                <span className="text-xs font-bold">{nameFor(c)}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <F testid="post-title" label={t("title")} value={form.title} onChange={set("title")} />
          <F testid="post-location" label={t("location")} value={form.location} onChange={set("location")} />
          <F testid="post-salary-min" label={t("salary") + " Min ₹"} type="number" value={form.salary_min} onChange={setNum("salary_min")} />
          <F testid="post-salary-max" label={t("salary") + " Max ₹"} type="number" value={form.salary_max} onChange={setNum("salary_max")} />
          <F testid="post-positions" label={t("positions")} type="number" value={form.positions} onChange={setNum("positions")} />
          <F testid="post-duration" label={t("duration_days")} type="number" value={form.duration_days} onChange={setNum("duration_days")} />
          <F testid="post-contact" label={t("contact_phone")} value={form.contact_phone} onChange={set("contact_phone")} />
        </div>

        <div className="mt-4">
          <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t("description")}</label>
          <textarea data-testid="post-desc" value={form.description} onChange={set("description")} rows={4} className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none" />
        </div>

        {/* -------- Premium upgrade -------- */}
        <div className={`mt-6 rounded-2xl border-2 p-5 transition-all ${form.is_premium ? "border-sarthi-primary bg-gradient-to-br from-orange-50 to-amber-50" : "border-dashed border-sarthi-border bg-sarthi-accent/30"}`}>
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              data-testid="post-premium-toggle"
              checked={form.is_premium}
              onChange={(e) => setForm((s) => ({ ...s, is_premium: e.target.checked }))}
              className="mt-1 h-6 w-6"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Sparkles className={`h-5 w-5 ${form.is_premium ? "text-sarthi-primary" : "text-sarthi-muted"}`} strokeWidth={2.5} />
                <span className="font-display text-lg font-extrabold text-sarthi-text">Make this a Premium post — ₹{PREMIUM_PRICE_INR}</span>
              </div>
              <p className="mt-1 text-sm font-medium text-sarthi-muted">
                Priority position in search results for 30 days · company logo · longer description · a "Premium" badge that workers can spot at a glance.
              </p>
            </div>
          </label>

          {form.is_premium && (
            <div className="mt-4 grid grid-cols-1 gap-4">
              <div>
                <label className="mb-1 flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-sarthi-muted">
                  <ImageIcon className="h-4 w-4" /> Company logo
                </label>
                <Uploader
                  testid="post-logo-uploader"
                  value={form.logo_file_id}
                  onChange={(v) => setForm((s) => ({ ...s, logo_file_id: v }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Extended description (Premium only)</label>
                <textarea
                  data-testid="post-long-desc"
                  value={form.long_description}
                  onChange={set("long_description")}
                  rows={3}
                  placeholder="Talk about your site, safety standards, accommodation, or anything that helps workers picture the job."
                  className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-sm font-semibold focus:border-sarthi-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Job site photos (up to 6)</label>
                <Uploader
                  testid="post-site-photos"
                  multiple
                  max={6}
                  value={form.site_photo_ids}
                  onChange={(v) => setForm((s) => ({ ...s, site_photo_ids: v }))}
                />
              </div>
            </div>
          )}
        </div>

        <button
          data-testid="post-submit"
          onClick={submit}
          disabled={busy}
          className="sarthi-btn-primary mt-6 w-full py-4 text-lg text-white disabled:opacity-50"
        >
          {busy ? "…" : form.is_premium ? `Post & pay ₹${PREMIUM_PRICE_INR}` : t("submit")}
        </button>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none" />
    </div>
  );
}
